// ============================================================================
// AUTHENTICATION SERVICE
// ============================================================================
// Business logic and API calls for Authentication and Authorization module

import { get, post, put, del, patch } from '../client/axiosClient';
import { authEndpoints, userEndpoints } from '../endpoints';
import { tokenUtils } from '../config/apiConfig';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  ListResponse,
  EmptyResponse,
  BulkOperationResponse,
  ID,
  User,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  LogoutRequest,
  LogoutResponse,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
  UserSession,
  PermissionCheckRequest,
  PermissionCheckResponse,
  DiscoverRequest,
  DiscoverResponse,
  AuthenticateRequest,
  AuthenticateResponse,
  RefreshHubTokenRequest,
} from '../types';
import { APPLICATION_ID } from '../types/auth.types';

/**
 * Authentication Service Class
 */
class AuthService {
  // ============================================================================
  // TENANT HUB AUTHENTICATION (Two-Step Flow)
  // ============================================================================

  /**
   * Step 1: Discover accessible tenants for the LMMS application.
   * The LMMS API proxies to TenantHub transparently — the response shape is
   * ApiResponse<DiscoverResponse>: { success, data: { accessibleTenants, ... } }.
   * The API reads the application ID from the X-App-ID header (set by the interceptor),
   * so the `applicationId` body field is only a fallback reference.
   */
  async discover(username: string, password: string, tenantId?: string): Promise<ApiResponse<DiscoverResponse>> {
    const request: DiscoverRequest = {
      username,
      password,
      clientId: APPLICATION_ID,
      tenantId,
    };

    const response = await post<any>(
      authEndpoints.discover(),
      request
    );

    // TenantHub response is proxied transparently by the LMMS API (already camelCase).
    // Shape: { success, message, data: { accessibleTenants, recommendedTenant, ... } }
    return response.data;
  }

  /**
   * Step 2: Authenticate with selected tenant
   */
  async authenticate(
    username: string,
    password: string,
    selectedTenantId: string
  ): Promise<ApiResponse<AuthenticateResponse>> {
    const request: AuthenticateRequest = {
      username,
      password,
      selectedTenantId,
      selectedApplicationId: APPLICATION_ID,
    };

    const response = await post<ApiResponse<AuthenticateResponse>>(
      authEndpoints.authenticate(),
      request
    );

    const authData = response.data;

    // Store tokens on successful authentication
    if (authData.success && authData.data) {
      tokenUtils.setToken(authData.data.accessToken);
      tokenUtils.setRefreshToken(authData.data.refreshToken);

      // Store tenant and application context
      localStorage.setItem('tenant_id', authData.data.tenantId || '');
      localStorage.setItem('tenant_name', authData.data.tenantName || '');
      localStorage.setItem('application_id', authData.data.applicationId || '');
    }

    return authData;
  }

  /**
   * Refresh Tenant Hub token
   */
  async refreshHubToken(tenantId?: string): Promise<ApiResponse<AuthenticateResponse>> {
    const refreshToken = tokenUtils.getRefreshToken();
    const storedTenantId = tenantId || localStorage.getItem('tenant_id') || '';

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const request: RefreshHubTokenRequest = {
      refreshToken,
      tenantId: storedTenantId,
      applicationId: APPLICATION_ID,
    };

    const response = await post<ApiResponse<AuthenticateResponse>>(
      authEndpoints.refreshHub(),
      request
    );

    const refreshData = response.data;

    // Store new tokens
    if (refreshData.success && refreshData.data) {
      tokenUtils.setToken(refreshData.data.accessToken);
      tokenUtils.setRefreshToken(refreshData.data.refreshToken);
    }

    return refreshData;
  }

  // ============================================================================
  // LEGACY AUTHENTICATION (Backward Compatibility)
  // ============================================================================

  /**
   * User login (DEPRECATED - use discover + authenticate)
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await post<LoginResponse>(authEndpoints.login(), credentials);
    const loginData = response.data;

    // Store tokens
    if (loginData.success && loginData.data) {
      tokenUtils.setToken(loginData.data.token);
      tokenUtils.setRefreshToken(loginData.data.refreshToken);
    }

    return loginData;
  }

  /**
   * User logout
   */
  async logout(request?: LogoutRequest): Promise<LogoutResponse> {
    const refreshToken = request?.refreshToken || tokenUtils.getRefreshToken();
    const response = await post<LogoutResponse>(authEndpoints.logout(), {
      refreshToken,
    });

    // Clear tokens, tenant context, and branch context
    tokenUtils.clearTokens();
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('tenant_name');
    localStorage.removeItem('application_id');
    localStorage.removeItem('entity_id');
    localStorage.removeItem('business_unit_id');
    localStorage.removeItem('business_unit_name');
    localStorage.removeItem('my_branches');

    return response.data;
  }

  /**
   * Register new user
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await post<RegisterResponse>(authEndpoints.register(), data);
    return response.data;
  }

  /**
   * Refresh access token
   */
  async refreshToken(request?: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    const refreshToken = request?.refreshToken || tokenUtils.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await post<RefreshTokenResponse>(authEndpoints.refresh(), {
      refreshToken,
    });

    const refreshData = response.data;

    // Store new tokens
    if (refreshData.success && refreshData.data) {
      tokenUtils.setToken(refreshData.data.token);
      tokenUtils.setRefreshToken(refreshData.data.refreshToken);
    }

    return refreshData;
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await get<ApiResponse<User>>(authEndpoints.me());
    return response.data;
  }

  /**
   * Update current user profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<ApiResponse<User>> {
    const response = await put<ApiResponse<User>>(authEndpoints.updateProfile(), data);
    return response.data;
  }

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordRequest): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.changePassword(), data);
    return response.data;
  }

  /**
   * Forgot password
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.forgotPassword(), data);
    return response.data;
  }

  /**
   * Reset password
   */
  async resetPassword(data: ResetPasswordRequest): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.resetPassword(), data);
    return response.data;
  }

  /**
   * Verify email
   */
  async verifyEmail(token: string): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.verifyEmail(), { token });
    return response.data;
  }

  /**
   * Resend verification email
   */
  async resendVerification(email: string): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(authEndpoints.resendVerification(), {
      email,
    });
    return response.data;
  }

  /**
   * Check permission
   */
  async checkPermission(
    request: PermissionCheckRequest
  ): Promise<ApiResponse<PermissionCheckResponse>> {
    const response = await post<ApiResponse<PermissionCheckResponse>>(
      authEndpoints.checkPermission(),
      request
    );
    return response.data;
  }

  /**
   * Get user sessions
   */
  async getSessions(): Promise<ListResponse<UserSession>> {
    const response = await get<ListResponse<UserSession>>(authEndpoints.sessions());
    return response.data;
  }

  /**
   * Revoke session
   */
  async revokeSession(sessionId: string): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(authEndpoints.revokeSession(sessionId));
    return response.data;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return tokenUtils.isAuthenticated();
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return tokenUtils.getToken();
  }

  // ============================================================================
  // USER MANAGEMENT METHODS (Admin)
  // ============================================================================

  /**
   * Get all users
   */
  async getUsers(params?: any): Promise<ListResponse<User>> {
    const endpoint = params
      ? buildEndpointWithQuery(userEndpoints.list(), params)
      : userEndpoints.list();

    const response = await get<ListResponse<User>>(endpoint);
    return response.data;
  }

  /**
   * Get user by ID
   */
  async getUserById(id: ID): Promise<ApiResponse<User>> {
    const response = await get<ApiResponse<User>>(userEndpoints.getById(id));
    return response.data;
  }

  /**
   * Create new user
   */
  async createUser(data: RegisterRequest): Promise<ApiResponse<User>> {
    const response = await post<ApiResponse<User>>(userEndpoints.create(), data);
    return response.data;
  }

  /**
   * Update user
   */
  async updateUser(id: ID, data: Partial<User>): Promise<ApiResponse<User>> {
    const response = await put<ApiResponse<User>>(userEndpoints.update(id), data);
    return response.data;
  }

  /**
   * Delete user
   */
  async deleteUser(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(userEndpoints.delete(id));
    return response.data;
  }

  /**
   * Search users
   */
  async searchUsers(params: any): Promise<ListResponse<User>> {
    const response = await post<ListResponse<User>>(userEndpoints.search(), params);
    return response.data;
  }

  /**
   * Get active users
   */
  async getActiveUsers(): Promise<ListResponse<User>> {
    const response = await get<ListResponse<User>>(userEndpoints.active());
    return response.data;
  }

  /**
   * Update user status
   */
  async updateUserStatus(id: ID, isActive: boolean): Promise<EmptyResponse> {
    const response = await patch<EmptyResponse>(userEndpoints.updateStatus(id), {
      isActive,
    });
    return response.data;
  }

  /**
   * Assign role to user
   */
  async assignRole(id: ID, role: string): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(userEndpoints.assignRole(id), { role });
    return response.data;
  }

  /**
   * Assign permissions to user
   */
  async assignPermissions(id: ID, permissions: string[]): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(userEndpoints.assignPermissions(id), {
      permissions,
    });
    return response.data;
  }

  /**
   * Bulk delete users
   */
  async bulkDeleteUsers(ids: ID[]): Promise<BulkOperationResponse> {
    const response = await del<BulkOperationResponse>(userEndpoints.bulkDelete(), {
      data: { ids },
    });
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const authService = new AuthService();
export default authService;
